import { useEffect, useRef } from 'react';
import { findUnlinkedNestedCampaigns } from '../../utils/nestedCampaignInvariant';

const RECOVERY_DEBOUNCE_MS = 2500;

/**
 * After startup, trash unlinked duplicate nests (same name as a real sibling).
 * Other ghosts are listed in Trash → Hidden from sidebar — you pick which to kill.
 * Does not full-download parents on launch.
 */
export function useOrphanCampaignRecovery({
  sprints,
  dataLoading,
  isLoadingViewData,
  isInitialStartupComplete,
  handleDeleteSprint,
}) {
  const fixedIdsRef = useRef(new Set());
  const recoveryTimerRef = useRef(null);

  useEffect(() => {
    if (dataLoading || isLoadingViewData || !isInitialStartupComplete) return;

    const duplicateGhosts = findUnlinkedNestedCampaigns(sprints).filter(
      (row) => row.isNameDuplicate && !fixedIdsRef.current.has(row.sprint.id)
    );
    if (duplicateGhosts.length === 0 || !handleDeleteSprint) return;

    if (recoveryTimerRef.current) {
      clearTimeout(recoveryTimerRef.current);
    }

    recoveryTimerRef.current = setTimeout(async () => {
      console.warn(
        `[NEST INVARIANT] Trashing ${duplicateGhosts.length} unlinked duplicate nested campaign(s) (same name as a mission-linked sibling):`,
        duplicateGhosts.map((row) => ({
          id: row.sprint.id,
          name: row.sprint.name,
          parentSprintId: row.sprint.parentSprintId,
          linkedSiblingId: row.linkedSibling?.id,
        }))
      );
      for (const row of duplicateGhosts) {
        fixedIdsRef.current.add(row.sprint.id);
        try {
          await handleDeleteSprint(row.sprint.id, { preserveLinkedMission: true });
        } catch (error) {
          console.error('[NEST INVARIANT] Failed to trash unlinked duplicate:', row.sprint.id, error);
        }
      }
    }, RECOVERY_DEBOUNCE_MS);

    return () => {
      if (recoveryTimerRef.current) {
        clearTimeout(recoveryTimerRef.current);
        recoveryTimerRef.current = null;
      }
    };
  }, [
    sprints,
    dataLoading,
    isLoadingViewData,
    isInitialStartupComplete,
    handleDeleteSprint,
  ]);
}

export default useOrphanCampaignRecovery;
